import React from "react";
import type { UsageData } from "../types";
import { Table, TableHeader, TableBody, TableRow, TableCell } from "../../../ui";

interface UsageByModelTableProps {
  usageByModelCore: UsageData["usage_by_model_core"];
  caching: UsageData["caching"];
}

const intFmt = (n: number) => n.toLocaleString();
const creditsFmt = (n: number) =>
  n.toLocaleString(undefined, { maximumFractionDigits: 4 });

/** Per-model_core rollup with Requests / Tokens / Credits and a totals row.
 * Sorted by credits descending so the dominant cost lines come first.
 *
 * Per design doc 013 §1d: rows are model_core identifiers in their raw form
 * (e.g. align-20260109). We deliberately don't humanise them — the raw string
 * is what customers type into their API requests, so it's the link they care
 * about. */
export function UsageByModelTable({ usageByModelCore, caching }: UsageByModelTableProps) {
  const entries = Object.entries(usageByModelCore).sort(
    ([, a], [, b]) => b.credits - a.credits,
  );

  const cachedTokensFor = (modelCore: string) =>
    caching.by_model_core[modelCore]?.cached_tokens ?? 0;

  const totals = entries.reduce(
    (acc, [modelCore, v]) => ({
      requests: acc.requests + v.requests,
      tokens: acc.tokens + v.tokens,
      cachedTokens: acc.cachedTokens + cachedTokensFor(modelCore),
      credits: acc.credits + v.credits,
    }),
    { requests: 0, tokens: 0, cachedTokens: 0, credits: 0 },
  );

  return (
    <Table>
      <TableHeader>
        <TableCell>Model</TableCell>
        <TableCell align="right">Requests</TableCell>
        <TableCell align="right">Tokens used</TableCell>
        <TableCell align="right">Cached tokens</TableCell>
        <TableCell align="right">Credits</TableCell>
      </TableHeader>
      <TableBody>
        {entries.length === 0 ? (
          <TableRow>
            <TableCell colSpan={5} align="center" className="py-8 text-muted-foreground">
              No usage data available for this month.
            </TableCell>
          </TableRow>
        ) : (
          <>
            {entries.map(([modelCore, v]) => (
              <TableRow key={modelCore}>
                <TableCell className="font-medium font-mono">
                  {modelCore}
                </TableCell>
                <TableCell align="right" className="font-mono">
                  {intFmt(v.requests)}
                </TableCell>
                <TableCell align="right" className="font-mono">
                  {intFmt(v.tokens)}
                </TableCell>
                <TableCell align="right" className="font-mono">
                  {cachedTokensFor(modelCore) > 0 ? intFmt(cachedTokensFor(modelCore)) : "—"}
                </TableCell>
                <TableCell align="right" className="font-mono">
                  {creditsFmt(v.credits)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell className="font-semibold">Total</TableCell>
              <TableCell align="right" className="font-mono font-semibold">
                {intFmt(totals.requests)}
              </TableCell>
              <TableCell align="right" className="font-mono font-semibold">
                {intFmt(totals.tokens)}
              </TableCell>
              <TableCell align="right" className="font-mono font-semibold">
                {totals.cachedTokens > 0 ? intFmt(totals.cachedTokens) : "—"}
              </TableCell>
              <TableCell align="right" className="font-mono font-semibold">
                {creditsFmt(totals.credits)}
              </TableCell>
            </TableRow>
          </>
        )}
      </TableBody>
    </Table>
  );
}
